/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/naming-convention */
import { useMemo } from 'react';
import { batch } from 'react-redux';
import {
    configureSummaryValues,
    setMqttTopics,
    setSummaryWidgetDetailsLoader,
} from '../features/dashboardManagement/commonDashboardSlice';

import {
    getFromToFormat,
    getMqttTopiceByWidgetType,
    getTimeInFromTo,
    isNullOrUndefined,
    systemTimezone,
    timeStampHandler,
} from '../utils/helpers';
import { getWidgetScoreQueryParams } from '../features/score/scoreHelpers';
import '../features/score/scoreApi';
import {
    toEnvironmentalStressMonitoringWidgetData,
    type EnvironmentalFilterResponse,
    type EnvironmentalMetricsResponse,
} from '../features/prediction/environmentalStressMonitoringApi';
import { buildLoadGroupMqttTopics } from '../utils/loadGroupMqtt';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { configureValues, setWidgetDetailsLoader } from '@fiji/common/src/features/dashboardManagement/dashboardSlice';
import { selectedOrg } from '../features/orgManagement/orgSlice';
import { useGetDeviceByIdQuery, useGetSearchedDeviceOrGroupQuery } from '../features/deviceManagement/deviceApi';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { useLazyGetGroupDetailsQuery } from '../features/group/groupApi';
import {
    useLazyGetLoadTrendConfigurationQuery,
    useLazyGetLoadTrendUnitsQuery,
} from '../features/deviceManagement/ontologyApi';
import { useLazyGetLoadGroupsListAllQuery } from '../features/loadGroups/loadGroupsApi';
type ReturnProps = {
    payload: any;
    response: any;
};

export const useGetWidgetDetails = ({
    selectorType,
    dataGetter,
}: {
    selectorType: string;
    dataGetter: any;
}): ReturnProps => {
    const widgetsClone = useTypedSelector((state) => state[selectorType]?.widgetList);
    const selectedNode = useTypedSelector((state) => state.common.selectedNode);
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const { data: deviceData }: any = useGetDeviceByIdQuery(dataGetter['deviceId'], {
        skip: !dataGetter['deviceId'],
    });
    const [getGroupDetails] = useLazyGetGroupDetailsQuery();
    const widgetFilterEntityIds = useMemo(
        () =>
            Array.from(
                new Set(
                    widgetsClone
                        ?.map((widget: any) => Object.values(widget?.config?.filters ?? {})?.flat(Infinity))
                        ?.flat(Infinity)
                        ?.filter(Boolean) ?? []
                )
            ),
        [widgetsClone]
    );
    const { data: searchedEntities }: any = useGetSearchedDeviceOrGroupQuery(
        { entityIds: widgetFilterEntityIds },
        { skip: !widgetFilterEntityIds?.length }
    );

    const dispatch = useAppDispatch();
    const currentOrg = useTypedSelector(selectedOrg);
    const { data: profileData }: any = useGetUserProfileQuery({}, { skip: !currentOrg?.id });
    const [getLoadTrendConfiguration] = useLazyGetLoadTrendConfigurationQuery();

    const [getUnits] = useLazyGetLoadTrendUnitsQuery();
    const [getAllLoadGroups] = useLazyGetLoadGroupsListAllQuery();

    type GenericObject = Record<string, unknown>;
    type UpcomingPayloadData = {
        records?: unknown[];
        total?: number;
    };

    const toObject = (value: unknown): GenericObject =>
        value && typeof value === 'object' && !Array.isArray(value) ? (value as GenericObject) : {};

    const getUpcomingFilters = (selectedWidgetConfig: unknown): GenericObject => {
        const config = toObject(selectedWidgetConfig);
        const filters = toObject(config.filters);

        if (Object.keys(filters).length) {
            return { ...filters };
        }

        if (dataGetter['deviceId']) {
            const wrapperKey = deviceData?.data?.wrapperDeviceType === 'Gateway' ? 'gatewayIds' : 'deviceIds';
            return { [wrapperKey]: [dataGetter['deviceId']] };
        }

        if (dataGetter['groupId']) {
            return { groupId: [dataGetter['groupId']] };
        }

        return {};
    };

    const getUpcomingPayload = (
        selectedWidgetConfig: unknown
    ): { body: { page: number; size: number; filters: GenericObject } } => {
        const config = toObject(selectedWidgetConfig);
        const maxCount = typeof config.maxCount === 'number' ? config.maxCount : 10;

        return {
            body: {
                page: 0,
                size: maxCount,
                filters: getUpcomingFilters(config),
            },
        };
    };

    const getUpcomingPayloadData = (response: unknown): UpcomingPayloadData => {
        if (Array.isArray(response)) {
            return { records: response };
        }

        return (response && typeof response === 'object' ? response : {}) as UpcomingPayloadData;
    };

    const getUpcomingPredictionRecords = (payloadData: UpcomingPayloadData): unknown[] =>
        Array.isArray(payloadData.records) ? payloadData.records : [];

    const mapUpcomingPredictionRecords = (records: unknown[]): GenericObject[] =>
        records.map((record: unknown) => {
            const mappedRecord = toObject(record);

            return {
                ...mappedRecord,
                id: mappedRecord.id ?? mappedRecord.predictionId ?? mappedRecord.upcomingId ?? '',
                deviceId: mappedRecord.deviceId ?? dataGetter['deviceId'] ?? '',
            };
        });

    const getUpcomingTotal = (payloadData: UpcomingPayloadData, mappedRecords: GenericObject[]): number => {
        if (typeof payloadData.total === 'number') {
            return payloadData.total;
        }

        return mappedRecords.length;
    };

    const getClonedWidget = (widgetId: string): any =>
        JSON.parse(JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {}));

    const getUpcomingRouteUrl = (isMultiDevice: boolean, widgetId: string): string => {
        if (isMultiDevice) {
            return `/${currentRealmName}/multiUpcoming/${String(widgetId)}`;
        }

        if (dataGetter['groupId']) {
            return `/${currentRealmName}/group/${dataGetter['groupId']}`;
        }

        if (dataGetter['deviceId']) {
            return `/${currentRealmName}/device/${dataGetter['deviceId']}`;
        }

        return `/${currentRealmName}/group/${currentOrg['id']}`;
    };

    function detailDispatcher(widgetId: any, config: any): void {
        if (selectorType === 'dashboard') {
            if (config && Object.keys(config)?.length) {
                dispatch(setWidgetDetailsLoader({ key: widgetId, value: 'loaded' }));
            }
            dispatch(configureValues({ widgetId: widgetId, config: config }));
        } else {
            batch(() => {
                dispatch(setSummaryWidgetDetailsLoader({ key: widgetId, value: 'loaded' }));
                dispatch(configureSummaryValues({ widgetId: widgetId, config: config }));
            });
        }
    }

    const getConfiguredGroupListNodes = (filters: any): any[] => {
        const configuredIds = [...(filters?.groupIds ?? []), ...(filters?.gatewayId ?? [])];
        const searchedRecords = searchedEntities?.data?.records ?? [];

        return configuredIds
            .map((id: string) => searchedRecords.find((record: any) => record?.id === id))
            .filter(Boolean);
    };

    const mergeGroupListRecords = (selectedWidget: any, response: any): any[] => {
        const responseRecords = response?.records ?? [];
        const missingConfiguredRecords = getConfiguredGroupListNodes(selectedWidget?.config?.filters).filter(
            (record: any) => !responseRecords.find((item: any) => item?.id === record?.id)
        );

        return [...responseRecords, ...missingConfiguredRecords];
    };

    function updateMultiDeviceChannel(
        selectedWidget: any,
        targetKey: string,
        selectedEntityIndex: number,
        selectedEntity: any,
        subChannel: any,
        channel: any
    ): void {
        const selectedIndex = selectedEntity?.channels?.findIndex(
            (subItem: any) => subItem.channelId === subChannel?.channelId
        );
        if (selectedIndex !== -1 && selectedEntityIndex !== -1) {
            selectedWidget.config[targetKey][selectedEntityIndex].channels[selectedIndex] = {
                ...selectedWidget.config[targetKey][selectedEntityIndex].channels[selectedIndex],
                value: subChannel?.value,
                ...(subChannel?.defaultValue && { defaultValue: subChannel.defaultValue }),
                ...(channel?.groupId && { groupId: channel.groupId }),
                ...(subChannel?.blockValue && { blockValue: subChannel.blockValue }),
                ...(subChannel?.description && { description: subChannel.description }),
                ...(subChannel?.tagId && { tagId: subChannel.tagId }),
                ...(subChannel?.channelDisplayName && { channelDisplayName: subChannel.channelDisplayName }),
                // FIJI-*: keep displayName in sync with edited instance ontology from /dm/v1/devices/details.
                ...(subChannel?.displayName && { displayName: subChannel.displayName }),
                unit: subChannel?.unit ?? '',
            };
        }
    }

    function updateSingleDeviceChannel(selectedWidget: any, targetKey: string, subChannel: any, channel: any): void {
        const selectedIndex1 = selectedWidget?.config?.[targetKey]?.[0]?.channels?.findIndex(
            (subItem: any) => subItem.channelId === subChannel?.channelId
        );
        if (selectedIndex1 !== -1) {
            selectedWidget.config[targetKey][0].channels[selectedIndex1] = {
                ...selectedWidget.config[targetKey][0].channels[selectedIndex1],
                value: subChannel?.value,
                ...(subChannel?.defaultValue && { defaultValue: subChannel.defaultValue }),
                ...(channel?.groupId && { groupId: channel.groupId }),
                ...(subChannel?.blockValue && { blockValue: subChannel.blockValue }),
                ...(subChannel?.description && { description: subChannel.description }),
                ...(subChannel?.tagId && { tagId: subChannel.tagId }),
                ...(subChannel?.channelDisplayName && { channelDisplayName: subChannel.channelDisplayName }),
                // FIJI-*: keep displayName in sync with edited instance ontology from /dm/v1/devices/details.
                ...(subChannel?.displayName && { displayName: subChannel.displayName }),
                unit: subChannel?.unit ?? '',
            };
        }
    }

    function detailHelper(selectedWidget: any, response: any, key?: any): void {
        const targetKey = key ?? 'secondary';

        response?.forEach((channel: any) => {
            const selectedEntity = selectedWidget?.config?.[targetKey]?.find(
                (item: any) => item.deviceId === channel.deviceId
            );
            const selectedEntityIndex = selectedWidget?.config?.[targetKey]?.findIndex(
                (item: any) => item.deviceId === channel.deviceId
            );

            channel?.channels?.forEach((subChannel: any) => {
                if (selectedWidget?.config?.multiDevice) {
                    updateMultiDeviceChannel(
                        selectedWidget,
                        targetKey,
                        selectedEntityIndex,
                        selectedEntity,
                        subChannel,
                        channel
                    );
                    return;
                }
                updateSingleDeviceChannel(selectedWidget, targetKey, subChannel, channel);
            });
        });
    }

    function detailsPayloadHelper(config: any, key: any, payload: any, extraArgs?: any): void {
        if (config) {
            if (!config?.['multiDevice']) {
                if (JSON.parse(JSON.stringify(payload))?.length) {
                    config[config[key] ? key : 'secondary']?.forEach((channel: any) => {
                        const selectedDeviceIndex = JSON.parse(JSON.stringify(payload))?.findIndex(
                            (device: any) => device?.deviceId === dataGetter['deviceId']
                        );

                        if (selectedDeviceIndex !== -1) {
                            if (payload[selectedDeviceIndex][extraArgs ?? 'channelId']) {
                                payload[selectedDeviceIndex][extraArgs ?? 'channelId'].push(
                                    ...channel.channels.map((item: any) => item.channelId)
                                );
                            } else {
                                payload[selectedDeviceIndex][extraArgs ?? 'channelId'] = channel.channels.map(
                                    (item: any) => item.channelId
                                );
                            }
                        }
                    });
                }
            } else {
                config[config[key] ? key : 'secondary']?.forEach((channel: any) => {
                    const selectedDeviceIndex = payload?.findIndex(
                        (device: any) => device?.deviceId === channel?.deviceId
                    );
                    if (selectedDeviceIndex !== -1) {
                        payload[selectedDeviceIndex][extraArgs ?? 'channelId'].push(
                            ...channel.channels.map((item: any) => item.channelId)
                        );
                    } else {
                        payload.push({
                            deviceId: config['multiDevice'] ? channel?.deviceId : dataGetter['deviceId'],
                            [extraArgs ?? 'channelId']: channel?.channels?.map((item: any) => item.channelId),
                        });
                    }
                });
            }
        }
    }

    function getDeviceDetailsPayload(config: any, selectedEntities: any = []): any[] {
        const payload: any = [];
        if (Array.isArray(selectedEntities?.selectedWidgetEntities)) {
            return selectedEntities?.selectedWidgetEntities.map((item: any) => ({
                deviceId: config?.['multiDevice'] ? item?.deviceId : dataGetter['deviceId'],
                channelId: item?.entityIds ?? [],
            }));
        }
        if (!config?.['multiDevice']) {
            payload.push({ deviceId: dataGetter['deviceId'] });
        }
        for (const key in config) {
            if (key === 'primary' || key === 'secondary') {
                detailsPayloadHelper(config, key, payload);
            }
        }
        return payload;
    }

    function getSelectedWidgetEntitiesPayload(config: any, selectedEntities: any, key = 'channelId'): any[] | null {
        if (!Array.isArray(selectedEntities?.selectedWidgetEntities)) {
            return null;
        }

        return selectedEntities.selectedWidgetEntities.map((item: any) => ({
            deviceId: config?.['multiDevice'] ? item?.deviceId : dataGetter['deviceId'],
            [key]: item?.entityIds ?? [],
        }));
    }

    function getDeviceSettingsPayload(config: any, key: string, extra?: any, selectedEntities?: any): any[] {
        const selectedWidgetEntitiesPayload = getSelectedWidgetEntitiesPayload(
            config,
            selectedEntities,
            extra ?? 'channelId'
        );
        if (selectedWidgetEntitiesPayload) {
            return selectedWidgetEntitiesPayload;
        }

        const payload: any = [];
        if (!config?.['multiDevice']) {
            payload.push({ deviceId: dataGetter['deviceId'] });
        }
        detailsPayloadHelper(config, key, payload, extra);
        return payload;
    }

    function getChartsDetailsPayload(config: any, selectedEntities?: any): any[] {
        const selectedWidgetEntitiesPayload = getSelectedWidgetEntitiesPayload(config, selectedEntities);
        if (selectedWidgetEntitiesPayload) {
            return selectedWidgetEntitiesPayload;
        }

        const payload: any = [];
        config?.secondary?.forEach((channel: any) => {
            if (config?.multiDevice) {
                const selectedDeviceIndex = payload?.findIndex((device: any) => device?.deviceId === channel?.deviceId);
                if (selectedDeviceIndex !== -1) {
                    payload[selectedDeviceIndex]['channelId'].push(channel.channelId);
                } else {
                    payload.push({
                        deviceId: channel?.deviceId,
                        channelId: [channel?.channelId],
                    });
                }
            } else {
                if (payload?.length) {
                    payload[0]['channelId'].push(channel.channelId);
                } else {
                    payload.push({
                        deviceId: dataGetter['deviceId'],
                        channelId: [channel?.channelId],
                    });
                }
            }
        });
        return payload;
    }

    function getGaugeDetailsPayload(config: any, selectedEntities?: any): any[] {
        const firstSelectedEntity = selectedEntities?.selectedWidgetEntities?.[0];
        const channelId = config?.data?.channelId;
        if (firstSelectedEntity?.entityIds?.length) {
            return [
                {
                    deviceId: config?.multiDevice ? firstSelectedEntity?.deviceId : dataGetter?.['deviceId'],
                    channelId: [firstSelectedEntity.entityIds[0]],
                },
            ];
        }

        if (!channelId) return [];

        return [
            {
                deviceId: config?.multiDevice ? config?.data?.deviceId : dataGetter?.['deviceId'],
                channelId: [channelId],
            },
        ];
    }

    const handleRelativeBemsPayloadForEnergyFlow = (assetTypes: any): Array<{ [key: string]: any }> => {
        const payload: Array<{ [key: string]: any }> = [];
        const assetTypesClone = JSON.parse(JSON.stringify(assetTypes ?? {}));
        for (const key in assetTypesClone) {
            assetTypesClone[key].forEach((item: any) => {
                if (item.isEnabled) {
                    payload.push({
                        deviceCategory: item.deviceCategory,
                        assetTypeId: item.id,
                        assetTypeIds: item.assetTypeIds,
                        other: key === 'primary' ? false : true,
                    });
                }
            });
        }
        return payload;
    };

    const handleRelativeBemsPayload = (assetTypes: any): Array<{ [key: string]: any }> => {
        const payload: Array<{ [key: string]: any }> = [];
        const assetTypesClone = JSON.parse(JSON.stringify(assetTypes ?? []));

        assetTypesClone?.forEach((item: any) => {
            if (item.isEnabled) {
                payload.push({
                    deviceCategory: item.deviceCategory,
                    assetTypeId: item.id,
                    other: false,
                    assetTypeIds: item.assetTypeIds,
                });
            }
        });

        return payload;
    };

    const handleLinkedBemsPayloadForEnergyFlow = (assets: any, assetTypes: any): Array<{ [key: string]: any }> => {
        const payload: Array<{ [key: string]: any }> = [];
        const assetsClone = JSON.parse(JSON.stringify(assets));
        const assetTypesClone = JSON.parse(JSON.stringify(assetTypes));
        processAssetTypesForEnergyFlow(assetTypesClone, assetsClone, payload);
        return payload;
    };

    const processAssetTypesForEnergyFlow = (
        assetTypesClone: any,
        assetsClone: any,
        payload: Array<{ [key: string]: any }>
    ): void => {
        for (const key in assetTypesClone) {
            assetTypesClone[key].forEach((item: any) => {
                processAssetsForEnergyFlow(item, key, assetsClone, payload);
            });
        }
    };

    const processAssetsForEnergyFlow = (
        item: any,
        key: string,
        assetsClone: any,
        payload: Array<{ [key: string]: any }>
    ): void => {
        if (!item.isEnabled) return;

        const selectedAssets = assetsClone.filter((asset: any) => item.assetTypeIds.includes(asset.deviceTypeId));
        if (selectedAssets.length === 0) return;

        const selectedIndex = payload.findIndex((preselectedAsset: any) =>
            item.assetTypeIds.includes(preselectedAsset.assetTypeId)
        );

        const assetIds = selectedAssets.map((subItem: any) => subItem.id);
        const assetTypeIds = Array.from(
            new Set(
                selectedAssets
                    .filter((asset: any) => item.assetTypeIds.includes(asset.deviceTypeId))
                    .map((asset: any) => asset.deviceTypeId)
            )
        );
        if (selectedIndex === -1) {
            payload.push({
                assetTypeId: item.id,
                other: key !== 'primary',
                assetIds,
                deviceCategory: item.deviceCategory,
                assetTypeIds: assetTypeIds,
            });
        } else {
            payload[selectedIndex] = {
                ...payload[selectedIndex],
                assetIds,
                deviceCategory: item.deviceCategory,
                assetTypeIds: assetTypeIds,
            };
        }
    };

    const handleLinkedBemsPayload = (assets: any, assetTypes: any): Array<{ [key: string]: any }> => {
        const payload: Array<{ [key: string]: any }> = [];
        const assetsClone = JSON.parse(JSON.stringify(assets));
        const assetTypesClone = JSON.parse(JSON.stringify(assetTypes));

        processAssetTypes(assetTypesClone, assetsClone, payload);
        return payload;
    };

    const processAssetTypes = (
        assetTypesClone: any,
        assetsClone: any,
        payload: Array<{ [key: string]: any }>
    ): void => {
        assetTypesClone.forEach((item: any) => {
            processAssets(item, assetsClone, payload);
        });
    };

    const processAssets = (item: any, assetsClone: any, payload: Array<{ [key: string]: any }>): void => {
        if (!item.isEnabled) return;

        const selectedAssets = assetsClone.filter((asset: any) => item.assetTypeIds.includes(asset.deviceTypeId));
        if (selectedAssets.length === 0) return;

        const selectedIndex = payload.findIndex((preselectedAsset: any) =>
            item.assetTypeIds.includes(preselectedAsset.assetTypeId)
        );
        const assetIds = selectedAssets.map((subItem: any) => subItem.id);
        const assetTypeIds = Array.from(
            new Set(
                selectedAssets
                    .filter((asset: any) => item.assetTypeIds.includes(asset.deviceTypeId))
                    .map((asset: any) => asset.deviceTypeId)
            )
        );

        if (selectedIndex === -1) {
            payload.push({
                assetTypeId: item.id,
                other: false,
                assetIds,
                deviceCategory: item.deviceCategory,
                assetTypeIds: assetTypeIds,
            });
        } else {
            payload[selectedIndex] = {
                ...payload[selectedIndex],
                assetIds,
                deviceCategory: item.deviceCategory,
                assetTypeIds: assetTypeIds,
            };
        }
    };

    function handleLoadsPayload(config: any): any[] {
        let payload: any = [];
        if (config?.selectedNodes?.length) {
            config?.selectedNodes?.forEach((item: any) => {
                const selectedIndex = payload?.findIndex((subItem: any) => subItem.deviceId === item.deviceId);
                const selectedItem = payload?.find((subItem: any) => subItem.deviceId === item.deviceId);
                if (selectedItem) {
                    payload?.[selectedIndex]?.loadId?.push(item.loadId);
                } else {
                    payload?.push({ deviceId: item.deviceId, loadId: [item.loadId] });
                }
            });
        } else {
            payload = [
                {
                    deviceId: dataGetter['deviceId'],
                    ...(!config?.isAutomated && {
                        loadId:
                            config?.secondary &&
                            Array.isArray(config?.secondary) &&
                            config?.secondary?.every((item: any) => typeof item === 'object')
                                ? config?.secondary?.map((item: any) => item.loadId)
                                : config?.secondary ?? [],
                    }),
                },
            ];
        }
        return payload;
    }

    function handleLoadGroupsPayload(config: any): any {
        const payload: any = {
            page: 0,
            size: 25,
        };
        if (config?.selectedNodes?.length) {
            payload['ids'] = config?.selectedNodes?.map((item: any) => item?.id);
        } else {
            if (dataGetter['deviceId']) payload['deviceId'] = [dataGetter['deviceId']];
            if (dataGetter['groupId']) payload['groupId'] = [dataGetter['groupId']];
            if (!config?.isAutomated) payload['ids'] = config?.secondary?.map((item: any) => item?.id);
        }
        if (!config?.isAutomated && !payload?.ids?.length) {
            return { page: 0, size: 0 };
        }
        return payload;
    }
    const getLoadGroupTrendsPayload = async (config: any): Promise<any> => {
        const payload: any = [];
        const { data: loadGroups }: any = await getAllLoadGroups(handleLoadGroupsPayload(config));
        if (loadGroups?.data?.records?.length) {
            const selectedGroup = loadGroups?.data?.records?.[0];
            if (!selectedGroup?.modelCategory) {
                return payload;
            }
            const deviceTypeIds = Array.from(
                new Set(
                    selectedGroup?.loadConfig
                        ?.map((item: any) => item?.deviceTypeId)
                        ?.filter((item: any) => Boolean(item)) ?? []
                )
            );
            if (!deviceTypeIds.length) {
                return payload;
            }
            const { data: unitsResponse } = await getUnits(deviceTypeIds);
            const configResult: any = await getLoadTrendConfiguration({
                modelCategory: selectedGroup?.modelCategory,
            });
            const configResponse = configResult?.data;
            const unit = Object.keys(unitsResponse?.data ?? {});
            selectedGroup?.loadConfig?.forEach((item: any) => {
                const index = payload?.findIndex((subItem: any) => subItem?.deviceId === item?.deviceId);
                const channelId = configResponse?.data?.[item?.deviceTypeId]?.[`${item?.loadId}_${unit[0]}`];
                if (channelId) {
                    if (index === -1) {
                        payload.push({
                            deviceId: item?.deviceId,
                            channelId: [channelId],
                        });
                    } else {
                        payload[index].channelId?.push(channelId);
                    }
                }
            });
        }
        return payload;
    };
    const getEnergyFlowTimestamp = (selectedWidgetConfig: any, widgetType: any): any => {
        if (widgetType === 'energy_flow' || widgetType === 'schedule') return undefined;
        if (widgetType === 'load_trends') {
            return timeStampHandler(
                profileData?.data?.timezone ?? systemTimezone,
                profileData?.data?.calendarWeek,
                selectedWidgetConfig?.selectedTimePeriod,
                null,
                null,
                'load_trends'
            );
        }
        return timeStampHandler(
            profileData?.data?.timezone ?? systemTimezone,
            profileData?.data?.calendarWeek,
            selectedWidgetConfig?.selectedTimePeriod
        );
    };

    const applyEnergyFlowTimeFields = (
        payload: { [key: string]: any },
        widgetType: any,
        result: any,
        selectedWidgetConfig: any
    ): void => {
        if (widgetType === 'metrics' || widgetType === 'load_trends') {
            payload['currentState'] = { startTime: result?.from, endTime: result?.to };
            payload['previousState'] = { startTime: result?.prevFrom, endTime: result?.prevTo };
            payload['timeFilter'] = selectedWidgetConfig?.selectedTimePeriod?.toUpperCase();
        } else if (widgetType === 'consumption_breakdown' || widgetType === 'aggregated_trends') {
            payload['startTime'] = result?.from;
            payload['endTime'] = result?.to;
            payload['timeFilter'] = selectedWidgetConfig?.selectedTimePeriod?.toUpperCase();
            if (widgetType === 'aggregated_trends') {
                payload['unit'] = selectedWidgetConfig?.unitType;
                if (!selectedWidgetConfig?.multiDevice) {
                    payload['latitude'] = selectedNode?.lat;
                    payload['longitude'] = selectedNode?.lng;
                }
            }
        }
    };

    const getEnergyFlowPayload = (selectedWidgetConfig: any, widgetType: any): any => {
        const result = getEnergyFlowTimestamp(selectedWidgetConfig, widgetType);
        const payload: { [key: string]: any } = {
            ...(!selectedWidgetConfig?.multiDevice && {
                parentId: dataGetter['deviceId'] ?? dataGetter['groupId'],
                parentType: dataGetter['deviceId'] ? 'GATEWAY' : 'GROUP',
            }),
        };
        if (selectedWidgetConfig?.multiDevice) {
            payload['assets'] =
                widgetType === 'energy_flow'
                    ? handleLinkedBemsPayloadForEnergyFlow(
                          selectedWidgetConfig?.assets,
                          selectedWidgetConfig?.assetTypes
                      )
                    : handleLinkedBemsPayload(selectedWidgetConfig?.assets, selectedWidgetConfig?.assetTypes);
        } else {
            payload['assets'] =
                widgetType === 'energy_flow'
                    ? handleRelativeBemsPayloadForEnergyFlow(selectedWidgetConfig?.assetTypes)
                    : handleRelativeBemsPayload(selectedWidgetConfig?.assetTypes);
        }
        if (widgetType !== 'weather' && widgetType !== 'schedule') payload['widgetTypeId'] = widgetType;
        applyEnergyFlowTimeFields(payload, widgetType, result, selectedWidgetConfig);
        return payload;
    };

    const getWeatherPayload = async (selectedWidgetConfig: any): Promise<any> => {
        const payload: any = {
            unit: profileData?.data?.temperatureUnit === 'F°(Fahrenheit)' ? 'F' : 'C',
            timeFilter: selectedWidgetConfig?.selectedTimePeriod?.toUpperCase(),
        };

        if (selectedWidgetConfig?.multiDevice) {
            const { data } = await getGroupDetails(selectedWidgetConfig?.assets?.id);
            if (data) {
                payload['latitude'] = data?.data?.lat;
                payload['longitude'] = data?.data?.lng;
            }
        } else {
            payload['latitude'] = selectedNode?.lat;
            payload['longitude'] = selectedNode?.lng;
        }

        return payload;
    };
    function handleTimelineCountsPayload(config: any): any {
        if (dataGetter['groupId'] === currentOrg?.id && !config?.multiDevice) {
            return { organizationId: currentOrg.id };
        }
        if (Object.keys(config?.filters ?? {})?.length) {
            return config?.filters;
        }
        if ((!config?.filters || !Object.keys(config?.filters ?? {})?.length) && dataGetter['deviceId']) {
            return {
                [deviceData?.data?.wrapperDeviceType === 'Gateway' ? 'gatewayId' : 'deviceId']: [
                    dataGetter['deviceId'],
                ],
            };
        }
        return { groupId: [dataGetter?.['groupId']] };
    }

    const getEnvironmentalStressMonitoringDeviceId = (config: any, selectedEntities: any): string | undefined => {
        const getDeviceId = (value: unknown): string | undefined =>
            typeof value === 'string' && value.length > 0 ? value : undefined;
        const getFirstDeviceId = (value: unknown): string | undefined =>
            Array.isArray(value) ? getDeviceId(value[0]) : getDeviceId(value);
        const selectedDeviceId = getFirstDeviceId(selectedEntities?.deviceId);
        const configuredDeviceId = getFirstDeviceId(config?.filters?.deviceIds) ?? getDeviceId(config?.deviceId);

        return config?.multiDevice
            ? selectedDeviceId ?? configuredDeviceId
            : getDeviceId(dataGetter['deviceId']) ?? configuredDeviceId;
    };

    const payload = useMemo(
        () => ({
            details: (selectedWidgetConfig: any, selectedEntities?: any): any[] =>
                getDeviceDetailsPayload(
                    selectedWidgetConfig?.config ?? selectedWidgetConfig,
                    selectedEntities ?? selectedWidgetConfig?.selectedEntities
                ),
            properties: (selectedWidgetConfig: any, selectedEntities?: any): any[] =>
                getDeviceSettingsPayload(
                    selectedWidgetConfig?.config ?? selectedWidgetConfig,
                    'secondary',
                    undefined,
                    selectedEntities ?? selectedWidgetConfig?.selectedEntities
                ),
            settings: (): any => ({ deviceId: dataGetter['deviceId'] }),
            command_bar: (selectedWidgetConfig: any, selectedEntities?: any): any[] =>
                getDeviceSettingsPayload(
                    selectedWidgetConfig?.config ?? selectedWidgetConfig,
                    'secondary',
                    undefined,
                    selectedEntities ?? selectedWidgetConfig?.selectedEntities
                ),
            'command_bar-getAvailableCommands': (selectedWidgetConfig: any, selectedEntities?: any): any[] =>
                getDeviceSettingsPayload(
                    selectedWidgetConfig?.config ?? selectedWidgetConfig,
                    'controlsConfig',
                    'channelIds',
                    selectedEntities ?? selectedWidgetConfig?.selectedEntities
                ),
            device_list: (selectedWidgetConfig: any): any => {
                return {
                    page: 0,
                    size: selectedWidgetConfig?.maxCount,
                    ...(selectedWidgetConfig?.filters && { filters: { ...selectedWidgetConfig?.filters } }),
                    ...(!selectedWidgetConfig?.multiDevice &&
                        (dataGetter['deviceId'] &&
                            deviceData?.data?.wrapperDeviceType === 'Gateway' && {
                                filters: {
                                    parentId: [dataGetter['deviceId']],
                                    ...(selectedWidgetConfig?.filters && selectedWidgetConfig?.filters),
                                },
                            },
                        dataGetter['deviceId'] &&
                            deviceData?.data?.wrapperDeviceType !== 'Gateway' && {
                                filters: {
                                    deviceIds: [dataGetter['deviceId']],
                                    ...(selectedWidgetConfig?.filters && selectedWidgetConfig?.filters),
                                },
                            },
                        dataGetter['groupId'] &&
                            dataGetter['groupId'] !== currentOrg?.id && {
                                filters: {
                                    groups: [dataGetter['groupId']],
                                    ...(selectedWidgetConfig?.filters && selectedWidgetConfig?.filters),
                                },
                            })),
                    ...(selectedWidgetConfig?.sortPayload &&
                        Object.keys(selectedWidgetConfig?.sortPayload)?.length && {
                            sort: selectedWidgetConfig?.sortPayload,
                        }),
                };
            },
            schedule: (): any => {
                return {
                    filters: {
                        ...(dataGetter['deviceId'] && deviceData?.data?.wrapperDeviceType !== 'Gateway'
                            ? {
                                  deviceId: [dataGetter['deviceId']],
                              }
                            : dataGetter['deviceId'] && {
                                  gatewayId: [dataGetter['deviceId']],
                              }),

                        ...(dataGetter['groupId'] && { groupId: [dataGetter['groupId']] }),
                        ...getFromToFormat('day', Date.now()),
                    },
                    page: 0,
                    size: 4,
                };
            },
            //TODO
            gauge: (selectedWidgetConfig: any, selectedEntities?: any): any[] =>
                getGaugeDetailsPayload(
                    selectedWidgetConfig?.config ?? selectedWidgetConfig,
                    selectedEntities ?? selectedWidgetConfig?.selectedEntities
                ),
            charts: (selectedWidgetConfig: any, selectedEntities?: any): any[] =>
                getChartsDetailsPayload(
                    selectedWidgetConfig?.config ?? selectedWidgetConfig,
                    selectedEntities ?? selectedWidgetConfig?.selectedEntities
                ),
            trends: (selectedWidgetConfig: any, selectedEntities?: any): any => ({
                payload: getChartsDetailsPayload(
                    selectedWidgetConfig?.config ?? selectedWidgetConfig,
                    selectedEntities ?? selectedWidgetConfig?.selectedEntities
                ),
                ...(selectedWidgetConfig &&
                    selectedWidgetConfig?.duration && {
                        from: getTimeInFromTo(selectedWidgetConfig?.duration ?? 24, 'h')?.from,
                        to: getTimeInFromTo(selectedWidgetConfig?.duration ?? 24, 'h')?.to,
                    }),
            }),
            'timeline-getDeviceAlertCount': (selectedWidgetConfig: any): any =>
                handleTimelineCountsPayload(selectedWidgetConfig),
            timeline: (selectedWidgetConfig: any): any => {
                return {
                    body: {
                        page: 0,
                        size: selectedWidgetConfig?.maxCount,
                        filters: {
                            ...(selectedWidgetConfig?.preferences && { severity: selectedWidgetConfig?.preferences }),
                            ...(selectedWidgetConfig?.filters
                                ? selectedWidgetConfig?.filters
                                : dataGetter?.['deviceId']
                                ? {
                                      [deviceData?.data?.wrapperDeviceType === 'Gateway' ? 'gatewayId' : 'deviceId']: [
                                          dataGetter?.['deviceId'],
                                      ],
                                  }
                                : { groupId: [dataGetter?.['groupId']] }),
                        },
                    },
                    ...(selectedWidgetConfig?.multiDevice && { params: { isLinkedWidget: true } }),
                };
            },
            group_list: (selectedWidgetConfig: any): any => ({
                page: 0,
                size: selectedWidgetConfig?.maxCount,
                ...(selectedWidgetConfig?.filters
                    ? { ...selectedWidgetConfig?.filters }
                    : dataGetter['deviceId'] && selectedNode?.wrapperDeviceType === 'Gateway'
                    ? { gatewayId: [dataGetter['deviceId']] }
                    : { relativeGroupId: dataGetter['groupId'] }),
                ...(selectedWidgetConfig?.sortPayload &&
                    Object.keys(selectedWidgetConfig?.sortPayload)?.length && {
                        sort: selectedWidgetConfig?.sortPayload,
                    }),
            }),
            map: (selectedWidgetConfig: any): any => ({
                topLeft: {
                    lat: 85.09376317181992,
                    lng: -180,
                },
                bottomRight: {
                    lat: -85.22772698604217,
                    lng: 180,
                },
                precision: 1,
                filters: {
                    ...(selectedWidgetConfig?.filters && selectedWidgetConfig?.multiDevice
                        ? selectedWidgetConfig?.filters
                        : dataGetter['groupId']
                        ? { ...selectedWidgetConfig?.filters, groups: [dataGetter['groupId']] }
                        : {
                              ...selectedWidgetConfig?.filters,
                              ...(selectedNode?.wrapperDeviceType === 'Gateway'
                                  ? { parentId: [dataGetter['deviceId']] }
                                  : {
                                        deviceIds: [dataGetter['deviceId']],
                                    }),
                          }),
                },
            }),
            loads: (selectedWidgetConfig: any): any[] => handleLoadsPayload(selectedWidgetConfig),
            load_group_trends: (selectedWidgetConfig: any): any[] => handleLoadGroupsPayload(selectedWidgetConfig),
            'load_group_trends-getLoadGroupTrends': async (selectedWidgetConfig: any): Promise<any> => ({
                payload: await getLoadGroupTrendsPayload(selectedWidgetConfig),
                from: getTimeInFromTo(selectedWidgetConfig?.duration ?? 24, 'h')?.from,
                to: getTimeInFromTo(selectedWidgetConfig?.duration ?? 24, 'h')?.to,
                loadTrend: true,
            }),
            load_groups: (selectedWidgetConfig: any): any[] => handleLoadGroupsPayload(selectedWidgetConfig),
            virtual_loads: (): any => ({
                ...(dataGetter['deviceId'] && { deviceId: dataGetter['deviceId'] }),
                page: 0,
                size: 50,
            }),
            upcoming: (selectedWidgetConfig: any): any => getUpcomingPayload(selectedWidgetConfig),
            'environmental_stress_monitoring-getEnvironmentalStressMonitoringMetrics': (
                config: any,
                selectedEntities: any
            ): any => {
                const deviceId = getEnvironmentalStressMonitoringDeviceId(config, selectedEntities);
                if (!deviceId) return undefined;

                return {
                    deviceId,
                    organizationId: currentOrg?.id,
                    window: config?.window ?? 'DAILY',
                    timeWindow: 30 * 24 * 60 * 60,
                };
            },
            'environmental_stress_monitoring-getEnvironmentalStressMonitoringFilter': (
                config: any,
                selectedEntities: any
            ): any => {
                const deviceId = getEnvironmentalStressMonitoringDeviceId(config, selectedEntities);
                if (!deviceId) return undefined;

                return {
                    deviceId,
                    organizationId: currentOrg?.id,
                    parameter: config?.parameter,
                    page: 0,
                    size: config?.maxCount ?? 10,
                    window: 'DAILY',
                    timeWindow: 30 * 24 * 60 * 60,
                    useOpenDateTimeWindow: false,
                };
            },
            components: (): any => ({ page: 0, size: 10 }),
            score: (selectedWidgetConfig: any): any => {
                return getWidgetScoreQueryParams(
                    selectedWidgetConfig?.filters,
                    dataGetter['deviceId'],
                    dataGetter['groupId']
                );
            },
            energy_flow: (selectedWidgetConfig: any): any => getEnergyFlowPayload(selectedWidgetConfig, 'energy_flow'),
            load_trends: (selectedWidgetConfig: any): any => getEnergyFlowPayload(selectedWidgetConfig, 'load_trends'),
            consumption_breakdown: (selectedWidgetConfig: any): any =>
                getEnergyFlowPayload(selectedWidgetConfig, 'consumption_breakdown'),
            aggregated_trends: (selectedWidgetConfig: any): any =>
                getEnergyFlowPayload(selectedWidgetConfig, 'aggregated_trends'),
            metrics: (selectedWidgetConfig: any): any => getEnergyFlowPayload(selectedWidgetConfig, 'metrics'),
            weather: (selectedWidgetConfig: any): any => getWeatherPayload(selectedWidgetConfig),
            default: (): any[] => [],
        }),
        [deviceData, dataGetter, currentOrg, currentRealmName]
    );

    function handleDetailValues(response: any, widgetId: any, key: string, referenceWidget?: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(referenceWidget ?? widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );

        const buildValidChannelsByDevice = (): Map<string, Set<string>> => {
            const validChannelsByDevice = new Map<string, Set<string>>();
            (Array.isArray(response) ? response : []).forEach((item: any) => {
                if (!item?.deviceId) return;
                validChannelsByDevice.set(
                    item.deviceId,
                    new Set((item?.channels ?? []).map((subChannel: any) => subChannel?.channelId).filter(Boolean))
                );
            });

            return validChannelsByDevice;
        };

        const reconcileGroupedConfigChannels = (configKey: string): void => {
            if (!selectedWidget?.config?.[configKey]) {
                return;
            }

            const validChannelsByDevice = buildValidChannelsByDevice();

            if (!selectedWidget?.config?.multiDevice) {
                const validChannelIds = new Set(
                    (Array.isArray(response) ? response : [])
                        .flatMap((item: any) => item?.channels ?? [])
                        .map((subChannel: any) => subChannel?.channelId)
                        .filter(Boolean)
                );

                if (Array.isArray(selectedWidget.config[configKey]?.[0]?.channels)) {
                    selectedWidget.config[configKey][0].channels = selectedWidget.config[configKey][0].channels.filter(
                        (subChannel: any) => validChannelIds.has(subChannel?.channelId)
                    );
                }
                return;
            }

            selectedWidget.config[configKey] = (selectedWidget.config[configKey] ?? [])
                .map((entity: any) => ({
                    ...entity,
                    channels: (entity?.channels ?? []).filter((subChannel: any) =>
                        (validChannelsByDevice.get(entity?.deviceId) ?? new Set<string>()).has(subChannel?.channelId)
                    ),
                }))
                .filter((entity: any) => entity?.channels?.length);
        };

        if (!selectedWidget?.config?.multiDevice && selectedWidget?.config) {
            selectedWidget.config.secondary = [
                {
                    deviceId: dataGetter['deviceId'],
                    channels: selectedWidget['config']?.['secondary']
                        ?.map((item: any) => item?.channels)
                        ?.flat(Infinity),
                },
            ];

            if (selectedWidget?.config?.primary?.length) {
                selectedWidget.config.primary = [
                    {
                        deviceId: dataGetter['deviceId'],
                        channels: selectedWidget['config']?.['primary']
                            ?.map((item: any) => item?.channels)
                            ?.flat(Infinity),
                    },
                ];
            }
        }

        if (['details', 'properties', 'settings'].includes(selectedWidget?.widgetType?.id)) {
            reconcileGroupedConfigChannels(key);
        }

        response?.forEach((channel: any) => {
            const selectedEntity = selectedWidget?.config?.[key]?.find(
                (item: any) => item.deviceId === channel.deviceId
            );
            const selectedEntityIndex =
                selectedWidget?.config?.[key]?.findIndex((item: any) => item.deviceId === channel.deviceId) ?? -1;

            channel?.channels?.forEach((subChannel: any) => {
                if (selectedWidget?.config?.multiDevice) {
                    const selectedIndex = selectedEntity?.channels?.findIndex(
                        (subItem: any) => subItem?.channelId === subChannel?.channelId
                    );

                    if (selectedIndex !== -1 && selectedEntityIndex !== -1) {
                        selectedWidget.config[key][selectedEntityIndex]['channels'][selectedIndex] = {
                            ...selectedWidget.config[key][selectedEntityIndex]['channels'][selectedIndex],
                            value: subChannel?.value,
                            ...(subChannel?.icon !== undefined && { icon: subChannel.icon }),
                            ...(subChannel?.defaultValue && { defaultValue: subChannel.defaultValue }),
                            // FIJI-*: keep displayName in sync with edited instance ontology from /dm/v1/devices/details.
                            ...(subChannel?.displayName && { displayName: subChannel.displayName }),
                            unit: subChannel?.unit ?? '',
                        };
                    }
                    return;
                }

                const selectedIndex = selectedWidget?.config?.[key][0]?.channels?.findIndex(
                    (subItem: any) => subItem?.channelId === subChannel?.channelId
                );

                if (!isNullOrUndefined(selectedIndex) && selectedIndex !== -1) {
                    selectedWidget.config[key][0]['channels'][selectedIndex] = {
                        ...selectedWidget.config[key][0]?.['channels'][selectedIndex],
                        value: subChannel?.value,
                        ...(subChannel?.icon !== undefined && { icon: subChannel.icon }),
                        ...(subChannel?.defaultValue && { defaultValue: subChannel.defaultValue }),
                        ...(subChannel?.displayName && { displayName: subChannel.displayName }),
                        unit: subChannel?.unit ?? '',
                    };
                }
            });
        });
        if (selectedWidget?.config?.route) {
            if (selectedWidget?.config?.multiDevice) {
                if (selectedWidget?.widgetType?.id === 'details') {
                    selectedWidget.config.route['url'] = `/${currentRealmName}/device-details`;
                } else if (selectedWidget?.widgetType?.id === 'properties') {
                    selectedWidget.config.route['url'] = `/${currentRealmName}/device-properties`;
                } else {
                    selectedWidget.config.route['url'] = `/${currentRealmName}/device-settings`;
                }
            } else {
                selectedWidget.config.route['url'] = `/${currentRealmName}/device/${dataGetter['deviceId']}`;
            }
        }
        if (selectedWidget?.['config']) {
            // Preserve widget's original updatedAt timestamp, fallback to current time if not set
            selectedWidget['config']['updatedAt'] = response?.[0]?.lastUpdated ?? new Date().getTime();
        }
        detailDispatcher(widgetId, selectedWidget?.config);
        if (selectedWidget?.config?.primary?.length && key === 'secondary') {
            handleDetailValues(response, widgetId, 'primary', selectedWidget);
            return;
        }
    }

    function handleCommandBar(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );

        const reconcileCommandBarChannels = (detailsResponse: any): void => {
            const validChannelsByDevice = new Map<string, Set<string>>();
            (Array.isArray(detailsResponse) ? detailsResponse : []).forEach((item: any) => {
                if (!item?.deviceId) return;
                validChannelsByDevice.set(
                    item.deviceId,
                    new Set((item?.channels ?? []).map((channel: any) => channel?.channelId).filter(Boolean))
                );
            });

            const reconcileKey = (configKey: string): void => {
                if (!selectedWidget?.config?.[configKey]) return;

                if (!selectedWidget?.config?.multiDevice) {
                    const validChannelIds = new Set(
                        (Array.isArray(detailsResponse) ? detailsResponse : [])
                            .flatMap((item: any) => item?.channels ?? [])
                            .map((channel: any) => channel?.channelId)
                            .filter(Boolean)
                    );

                    selectedWidget.config[configKey] = (selectedWidget.config[configKey] ?? []).map((entity: any) => ({
                        ...entity,
                        channels: (entity?.channels ?? []).filter((channel: any) =>
                            validChannelIds.has(channel?.channelId)
                        ),
                    }));
                    return;
                }

                selectedWidget.config[configKey] = (selectedWidget.config[configKey] ?? [])
                    .map((entity: any) => ({
                        ...entity,
                        channels: (entity?.channels ?? []).filter((channel: any) =>
                            (validChannelsByDevice.get(entity?.deviceId) ?? new Set<string>()).has(channel?.channelId)
                        ),
                    }))
                    .filter((entity: any) => entity?.channels?.length);
            };

            reconcileKey('secondary');
            reconcileKey('controlsConfig');
        };

        if (!selectedWidget?.config?.multiDevice && selectedWidget?.config) {
            selectedWidget.config.secondary = selectedWidget.config.secondary?.map((item: any) => {
                const itemClone = JSON.parse(JSON.stringify(item));
                itemClone.deviceId = dataGetter['deviceId'];
                return itemClone;
            });
        }

        if (selectedWidget?.config && !selectedWidget?.['config']['controlsConfig']) {
            selectedWidget['config']['controlsConfig'] = JSON.parse(
                JSON.stringify(selectedWidget['config']['secondary'])
            );
        }
        if (selectedWidget?.config) {
            response?.forEach((singleResponse: any) => {
                if (singleResponse?.endpointName !== 'getAvailableCommands') {
                    reconcileCommandBarChannels(singleResponse?.data?.data);
                    detailHelper(selectedWidget, singleResponse?.data?.data, 'controlsConfig');

                    singleResponse?.data?.data?.forEach((item: any) => {
                        dispatch(
                            setMqttTopics(
                                `${getMqttTopiceByWidgetType(selectedWidget?.widgetType?.id)}/${
                                    profileData?.data?.currentRealmId
                                }/${currentOrg?.id}/${item?.groupId}/${item['deviceId']}`
                            )
                        );
                    });
                } else {
                    if (!singleResponse?.isError && singleResponse?.data?.data?.controlResponses?.length) {
                        singleResponse?.data?.data?.controlResponses?.forEach((item: any) => {
                            //singleResponse?.originalArgs?.forEach((item: any) => {
                            let selectedDeviceIndex = JSON.parse(JSON.stringify(selectedWidget))?.config?.[
                                'controlsConfig'
                            ]?.findIndex((subItem: any) => subItem.deviceId === item.deviceId);
                            if (selectedWidget?.config?.multiDevice) {
                                selectedDeviceIndex = JSON.parse(JSON.stringify(selectedWidget))?.config?.[
                                    'controlsConfig'
                                ]?.findIndex((subItem: any) => subItem.deviceId === item.deviceId);
                            }
                            if (selectedDeviceIndex !== -1) {
                                selectedWidget.config.offlineAccess = item?.offline;

                                // BUG 2 FIX: Skip channelIds filter if a sendDeviceCommand is still in-flight.
                                // getAvailableCommands races ahead and returns post-command channelIds
                                // (e.g. "Turn On" removed), causing the in-flight button to disappear.
                                const liveSecondary =
                                    widgetsClone?.find((w: any) => w?.id === widgetId)?.config?.secondary ?? [];
                                const hasInFlightCommand = liveSecondary.some(
                                    (sec: any) => sec?.isLoading || sec?.channels?.some((ch: any) => ch?.isLoading)
                                );

                                if (!hasInFlightCommand) {
                                    selectedWidget.config.secondary[selectedDeviceIndex]['channels'] = JSON.parse(
                                        JSON.stringify(selectedWidget)
                                    )?.config?.controlsConfig[selectedDeviceIndex]?.channels?.filter(
                                        (filterdItem: any) => item?.channelIds?.includes(filterdItem.channelId)
                                    );
                                }
                            }
                        });
                    }
                }
            });
        }
        if (selectedWidget?.config?.secondary) {
            selectedWidget.config.secondary = selectedWidget.config.secondary.map((item: any) => {
                const outerRest = { ...item };
                delete outerRest.isLoading;
                if (outerRest?.channels) {
                    outerRest.channels = outerRest.channels.map((ch: any) => {
                        const chRest = { ...ch };
                        delete chRest.isLoading;
                        return chRest;
                    });
                }
                return outerRest;
            });
        }

        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function setGroupListRoute(selectedWidget: any, widgetId: any): void {
        if (!selectedWidget?.config?.route) return;
        if (!selectedWidget['config']['multiDevice']) {
            selectedWidget['config']['route']['url'] =
                dataGetter['deviceId'] && selectedNode?.wrapperDeviceType === 'Gateway'
                    ? `/${currentRealmName}/device/${dataGetter['deviceId']}`
                    : `/${currentRealmName}/groupDetails/${dataGetter['groupId']}`;
        } else {
            selectedWidget['config']['route']['url'] = `/${currentRealmName}/multiGroups/${widgetId}`;
        }
    }

    function setDeviceListRoute(selectedWidget: any, widgetId: any): void {
        if (!selectedWidget?.config) return;
        selectedWidget['config']['route'] = {};
        selectedWidget['config']['route']['state'] = 'devices';
        if (!selectedWidget?.config?.multiDevice) {
            selectedWidget['config']['route']['url'] = `/${currentRealmName}/${
                dataGetter['deviceId'] ? 'device' : 'group'
            }/${dataGetter['deviceId'] ? dataGetter['deviceId'] : dataGetter['groupId'] ?? currentOrg?.id}`;
        } else {
            selectedWidget['config']['route']['url'] = `/${currentRealmName}/multiDevices/${widgetId}`;
        }
    }

    function handleDeviceList(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        if (response && selectedWidget?.config) {
            selectedWidget.config['secondary'] =
                selectedWidget?.widgetType?.id === 'group_list'
                    ? mergeGroupListRecords(selectedWidget, response)
                    : response?.records ?? [];
            selectedWidget.config['total'] = response?.total;
        }
        if (selectedWidget?.widgetType?.id === 'group_list') {
            setGroupListRoute(selectedWidget, widgetId);
        } else {
            setDeviceListRoute(selectedWidget, widgetId);
        }
        detailDispatcher(widgetId, selectedWidget?.config);
    }
    function handleConsumptionBreakdown(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );

        if (selectedWidget?.config) {
            selectedWidget.config['secondary'] = response?.map((item: any) => ({
                ...item,
                value: parseFloat(item.value),
            }));
            if (selectedWidget?.config?.route)
                selectedWidget['config']['route']['url'] = `/${currentRealmName}/consumptionBreakdownDetails/${
                    dataGetter['deviceId'] ?? dataGetter['groupId']
                }`;
        }
        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleMap(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );

        if (selectedWidget?.config) {
            const records = Array.isArray(response) ? response : response?.records ?? [];
            selectedWidget['config']['secondary'] = records
                ?.filter?.((item: any) => item.coordinates)
                ?.map((group: any) => ({
                    id: group?.deviceList?.[0]?.id ?? '-',
                    name: group?.deviceList?.[0]?.name ?? group?.name ?? '-',
                    coordinates: group?.coordinates,
                    deviceCount: group.deviceCount,
                    icon: group?.icon,
                    groupPath: group?.groupPath,
                    tileKey: group?.tileKey,
                    deviceList: group?.deviceList,
                }));
            if (selectedWidget['config']['route']) {
                selectedWidget['config']['route']['url'] = `/${currentRealmName}/map/${widgetId}`;
            }
        }

        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleGaugeDetails(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        const gaugeChannel = response?.[0]?.channels?.[0];
        // const value: any = parseFloat(response[0]?.channels[0]?.value);
        // const parsedValue = Number.isInteger(value) ? parseInt(value) : value;
        if (selectedWidget?.config) {
            selectedWidget.config.data = {
                ...(selectedWidget?.config?.data ?? {}),
                channelId: gaugeChannel?.channelId,
                ...(gaugeChannel?.deviceId && { deviceId: gaugeChannel?.deviceId }),
                unit: gaugeChannel?.unit,
                value: gaugeChannel?.value !== 'nan' ? parseInt(gaugeChannel?.value) || 0 : 0,
            };
        }
        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleChartDetails(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        if (selectedWidget?.config) {
            if (!selectedWidget?.config?.multiDevice) {
                selectedWidget['config']['secondary'] = selectedWidget['config']['secondary']?.map((channel: any) => ({
                    ...channel,
                    deviceId: dataGetter['deviceId'],
                }));
            }

            const validChannelsByDevice = new Map<string, Set<string>>();
            (Array.isArray(response) ? response : []).forEach((channelData: any) => {
                validChannelsByDevice.set(
                    channelData?.deviceId,
                    new Set((channelData?.channels ?? []).map((channel: any) => channel?.channelId).filter(Boolean))
                );
            });

            selectedWidget.config.secondary = (selectedWidget.config.secondary ?? []).filter((item: any) => {
                const deviceId = selectedWidget?.config?.multiDevice ? item?.deviceId : dataGetter['deviceId'];
                const validChannelIds = validChannelsByDevice.get(deviceId);
                return validChannelIds ? validChannelIds.has(item?.channelId) : false;
            });

            response?.forEach((channelData: any) => {
                channelData?.channels?.forEach((channel: any) => {
                    let selectedEntityIndex = selectedWidget?.config?.secondary?.findIndex(
                        (item: any) => channel?.channelId === item?.channelId
                    );
                    if (selectedWidget?.config?.multiDevice) {
                        selectedEntityIndex = selectedWidget?.config?.secondary?.findIndex(
                            (item: any) =>
                                channel?.channelId === item?.channelId && channelData?.deviceId === item?.deviceId
                        );
                        if (selectedEntityIndex !== -1) {
                            selectedWidget['config']['secondary'][selectedEntityIndex] = {
                                ...selectedWidget['config']['secondary'][selectedEntityIndex],
                                unit: channel?.unit ?? '',
                                value: channel?.value !== 'nan' ? parseInt(channel?.value) : 0,
                                // FIJI-*: keep displayName in sync with edited instance ontology from /dm/v1/devices/details.
                                ...(channel?.displayName && { displayName: channel.displayName }),
                            };
                        }
                    } else {
                        if (selectedEntityIndex !== -1) {
                            selectedWidget['config']['secondary'][selectedEntityIndex] = {
                                ...selectedWidget['config']['secondary'][selectedEntityIndex],
                                unit: channel?.unit ?? '',
                                value: channel?.value !== 'nan' ? parseInt(channel?.value) : 0,
                                ...(channel?.displayName && { displayName: channel.displayName }),
                            };
                        }
                    }
                });
            });
            if (selectedWidget?.config?.route) {
                if (selectedWidget?.config?.multiDevice) {
                    if (selectedWidget?.widgetType?.id === 'charts') {
                        selectedWidget.config.route['url'] = `/${currentRealmName}/multiCharts/${widgetId}`;
                    } else {
                        selectedWidget.config.route['url'] = `/${currentRealmName}/device`;
                    }
                } else {
                    if (selectedWidget?.widgetType?.id === 'charts') {
                        selectedWidget.config.route[
                            'url'
                        ] = `/${currentRealmName}/chart/${dataGetter['deviceId']}/${widgetId}`;
                    } else {
                        selectedWidget.config.route['url'] = `/${currentRealmName}/device/${dataGetter['deviceId']}`;
                    }
                }
            }
        }
        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleTrends(widgetId: any, response: any, configurableKey: any): void {
        let selectedWidget = JSON.parse(JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {}));
        if (configurableKey?.selectedWidget) {
            selectedWidget = JSON.parse(JSON.stringify(configurableKey?.selectedWidget));
        }

        if (selectedWidget?.config) {
            if (!selectedWidget?.config?.multiDevice) {
                selectedWidget['config']['channelData'] = selectedWidget['config']['channelData']?.map(
                    (channel: any) => ({ ...channel, deviceId: dataGetter['deviceId'] })
                );
            }

            const validChannelsByDevice = new Map<string, Set<string>>();
            (Array.isArray(response) ? response : []).forEach((channelData: any) => {
                validChannelsByDevice.set(
                    channelData?.deviceId,
                    new Set((channelData?.channels ?? []).map((channel: any) => channel?.channelId).filter(Boolean))
                );
            });

            selectedWidget.config.secondary = (selectedWidget.config.secondary ?? []).filter((item: any) => {
                const deviceId = selectedWidget?.config?.multiDevice ? item?.deviceId : dataGetter['deviceId'];
                const validChannelIds = validChannelsByDevice.get(deviceId);
                return validChannelIds ? validChannelIds.has(item?.channelId) : false;
            });

            response?.forEach((channelData: any) => {
                channelData?.channels?.forEach((channel: any) => {
                    let selectedEntityIndex = selectedWidget?.config?.secondary?.findIndex(
                        (item: any) =>
                            channel.channelId === item.channelId && channelData?.deviceId === dataGetter['deviceId']
                    );

                    if (selectedWidget?.config?.multiDevice) {
                        selectedEntityIndex = selectedWidget?.config?.secondary?.findIndex(
                            (item: any) =>
                                channel.channelId === item.channelId && channelData?.deviceId === item?.deviceId
                        );
                        if (selectedEntityIndex !== -1) {
                            selectedWidget['config']['secondary'][selectedEntityIndex] = {
                                ...selectedWidget['config']['secondary'][selectedEntityIndex],
                                data: channel?.records ?? [],
                            };
                        }
                    } else {
                        if (!isNullOrUndefined(selectedEntityIndex) && selectedEntityIndex !== -1) {
                            selectedWidget['config']['secondary'][selectedEntityIndex] = {
                                ...selectedWidget['config']['secondary'][selectedEntityIndex],
                                data: channel?.records ?? [],
                                deviceId: dataGetter['deviceId'],
                            };
                        }
                    }
                });

                dispatch(
                    setMqttTopics(
                        `${getMqttTopiceByWidgetType(selectedWidget?.widgetType?.id)}/${
                            profileData?.data?.currentRealmId
                        }/${currentOrg?.id}/${deviceData?.data?.groupId ?? channelData?.groupId}/${
                            channelData?.['deviceId']
                        }/devices`
                    )
                );
            });
            if (configurableKey) {
                for (const key in configurableKey) {
                    if (!configurableKey?.selectedWidget) selectedWidget['config'][key] = configurableKey[key];
                }
            }
            if (selectedWidget?.config?.route) {
                if (selectedWidget?.config?.multiDevice) {
                    selectedWidget.config.route['url'] = `/${currentRealmName}/multiTrends/${widgetId}`;
                } else {
                    selectedWidget.config.route['url'] = `/${currentRealmName}/device/${dataGetter['deviceId']}`;
                }
            }
        }

        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleTimelineDetails(widgetId: any, responses: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        const widgetConfig = selectedWidget?.config;
        if (!widgetConfig) {
            detailDispatcher(widgetId, widgetConfig);
            return;
        }

        const getTimelineRouteUrl = (): string => {
            if (dataGetter['groupId']) {
                return `/${currentRealmName}/group/${dataGetter['groupId']}`;
            }

            if (dataGetter['deviceId']) {
                return `/${currentRealmName}/device/${dataGetter['deviceId']}`;
            }

            return `/${currentRealmName}/group/${currentOrg['id']}`;
        };

        responses.forEach((response: any) => {
            if (response.endpointName === 'getDeviceAlertCount') {
                widgetConfig['statusCount'] = {
                    alarms: response?.data?.data?.aggregations?.alarmCount ?? 0,
                    offline: response?.data?.data?.aggregations?.offlineCount ?? 0,
                    warnings: response?.data?.data?.aggregations?.warningCount ?? 0,
                    information: response?.data?.data?.aggregations?.informationalCount ?? 0,
                };
            } else {
                widgetConfig['secondary'] = response?.data?.data?.records;
            }

            widgetConfig['route'] = {};
            widgetConfig['detailRoute'] = `/${currentRealmName}/TimelineDetails`;
            if (!widgetConfig?.multiDevice) {
                widgetConfig['route']['state'] = 'timeline';
                widgetConfig['route']['url'] = getTimelineRouteUrl();
            } else {
                widgetConfig['route']['url'] = `/${currentRealmName}/multiTimeline/${widgetId}`;
            }
        });

        detailDispatcher(widgetId, widgetConfig);
    }

    const handleSchedule = (widgetId: any, response: any): void => {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        if (selectedWidget?.config) {
            selectedWidget['config']['secondary'] = response?.records;
            if (selectedWidget?.config?.route) {
                if (!selectedWidget?.config?.multiDevice) {
                    selectedWidget['config']['route']['url'] = `/${currentRealmName}/${
                        dataGetter['deviceId'] ? 'device' : 'group'
                    }/${dataGetter['deviceId'] ?? dataGetter['groupId']}`;
                } else {
                    selectedWidget['config']['route']['url'] = `/${currentRealmName}/device`;
                }
            }
        }

        detailDispatcher(widgetId, selectedWidget?.config);
    };

    function handleLoadGroups(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        if (selectedWidget?.config) {
            selectedWidget['config']['secondary'] = response?.records;
            response?.records?.forEach((record: any) => {
                const baseTopic = buildLoadGroupMqttTopics({
                    realmId: profileData?.data?.currentRealmId,
                    orgId: currentOrg?.id,
                    groupId: record?.groupId,
                    sourceGroupId: record?.sourceGroupId,
                    parentGroupId: record?.parentGroupId ?? record?.group?.parentGroupId,
                    sourceDeviceId: record?.sourceDeviceId,
                    deviceIds: record?.deviceIds,
                });
                if (baseTopic?.[0]?.length) {
                    dispatch(setMqttTopics(baseTopic?.[0]));
                }
            });

            if (!selectedWidget?.config?.multiDevice) {
                selectedWidget['config']['route'] = {
                    ...selectedWidget['config']['route'],
                };
                selectedWidget.config.route['url'] = `/${currentRealmName}/${
                    dataGetter['deviceId'] ? 'device' : 'group'
                }/${dataGetter['deviceId'] ?? dataGetter['groupId']}`;
                selectedWidget['config']['route']['state'] = dataGetter['deviceId'] ? 'loads' : 'load_groups';
                selectedWidget['config']['route']['subState'] = 'load_groups';
            } else {
                selectedWidget['config']['route'] = {
                    ...selectedWidget['config']['route'],
                };
                selectedWidget['config']['route']['state'] = '';
                selectedWidget.config.route['url'] = `/${currentRealmName}/multi-load-groups/${widgetId}`;
            }

            selectedWidget.config['detailRoute'] = `/${currentRealmName}/load-groups`;
        }

        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleLoadGroupTrends(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        if (selectedWidget?.config) {
            response?.forEach((singleResponse: any) => {
                if (singleResponse.endpointName !== 'getLoadGroupsListAll') {
                    const flattenChannels = (data: any): any => {
                        return data?.flatMap((item: any) =>
                            item?.channels?.map((channel: any) => ({
                                ...channel,
                                deviceId: item?.deviceId,
                            }))
                        );
                    };
                    selectedWidget['config']['loadGroupTrends'] = flattenChannels(singleResponse?.data?.data);
                } else {
                    selectedWidget['config']['secondary'] = singleResponse?.data?.data.records;

                    selectedWidget['config']['route'] = {
                        ...selectedWidget['config']['route'],
                    };
                    selectedWidget.config.route['url'] = `/${currentRealmName}/load-group-trends/${widgetId}`;
                }
            });
        }

        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleDeviceLoads(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        if (selectedWidget?.config) {
            selectedWidget['config']['secondary'] = response?.response
                ?.map((item: any) =>
                    item?.configurationList?.map((loadItem: any) => ({
                        ...loadItem,
                        deviceId: item?.deviceId,
                        offlineDevice: item?.offlineDevice,
                        groupPath: item?.groupPath,
                    }))
                )
                ?.flat(Infinity);

            if (selectedWidget?.config?.route) {
                if (!selectedWidget?.config?.multiDevice) {
                    selectedWidget.config.route['url'] = `/${currentRealmName}/device/${dataGetter['deviceId']}`;
                } else {
                    selectedWidget.config.route['url'] = `/${currentRealmName}/device`;
                }
            }
            selectedWidget.config['detailRoute'] = `/${currentRealmName}/loadDetails`;
        }

        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleVirtualLoads(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        const widgetConfig = selectedWidget?.config;
        if (!widgetConfig) {
            detailDispatcher(widgetId, widgetConfig);
            return;
        }

        widgetConfig['secondary'] = response?.records;

        if (widgetConfig?.route) {
            widgetConfig.route['url'] = `/${currentRealmName}/device/${dataGetter['deviceId']}`;
        }

        widgetConfig['detailRoute'] = `/${currentRealmName}/virtualLoadDetails/${dataGetter['deviceId']}`;

        detailDispatcher(widgetId, widgetConfig);
        // selectedWidget['config']['route']['url'] = `/${currentRealmName}/devicedashboard/${dataGetter['deviceId']}`;
    }

    function handleUpcoming(widgetId: any, response: any): void {
        const selectedWidget = getClonedWidget(widgetId);
        const widgetConfig = selectedWidget && selectedWidget.config ? selectedWidget.config : undefined;
        if (!widgetConfig) {
            detailDispatcher(widgetId, widgetConfig);
            return;
        }

        const payloadData = getUpcomingPayloadData(response);
        const predictionRecords = getUpcomingPredictionRecords(payloadData);
        const mappedRecords = mapUpcomingPredictionRecords(predictionRecords);

        widgetConfig['secondary'] = mappedRecords;
        widgetConfig['total'] = getUpcomingTotal(payloadData, mappedRecords);
        widgetConfig.route = widgetConfig.route || {};

        if (!widgetConfig.multiDevice) {
            widgetConfig.route['state'] = 'upcoming';
        }

        widgetConfig.route['url'] = getUpcomingRouteUrl(Boolean(widgetConfig.multiDevice), widgetId);
        detailDispatcher(widgetId, widgetConfig);
    }

    function handleEnvironmentalStressMonitoring(
        widgetId: string,
        response: { metrics?: EnvironmentalMetricsResponse; filter?: EnvironmentalFilterResponse }
    ): void {
        const selectedWidget = getClonedWidget(widgetId);
        const widgetConfig = selectedWidget?.config;
        if (!widgetConfig) {
            detailDispatcher(widgetId, widgetConfig);
            return;
        }

        Object.assign(widgetConfig, toEnvironmentalStressMonitoringWidgetData(response.metrics, response.filter));
        widgetConfig.organizationId = currentOrg?.id;
        const configuredDeviceIds = widgetConfig.filters?.deviceIds ?? widgetConfig.filters?.deviceId;
        const configuredDeviceId: unknown = Array.isArray(configuredDeviceIds)
            ? configuredDeviceIds[0]
            : configuredDeviceIds;
        const deviceId: string | undefined =
            typeof configuredDeviceId === 'string' && configuredDeviceId ? configuredDeviceId : dataGetter['deviceId'];
        widgetConfig.route = {
            ...(widgetConfig.route ?? {}),
            state: 'environmentalStressMonitoring',
            url: `/${currentRealmName}/environmental-stress-monitoring${
                deviceId ? `/${encodeURIComponent(deviceId)}` : ''
            }`,
        };
        widgetConfig.detailRoute = `/${currentRealmName}/environmental-stress-monitoring/details`;
        detailDispatcher(widgetId, widgetConfig);
    }

    function handleScore(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );

        if (selectedWidget?.config) {
            selectedWidget.config['data'] = response ?? {};
        }

        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleComponents(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );

        if (selectedWidget?.config) {
            selectedWidget.config['secondary'] = response;
        }

        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleLoadTrends(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        if (selectedWidget?.config) {
            const wrapperId = deviceData?.data?.wrapperDeviceType;
            selectedWidget.config['secondary'] = response;
            selectedWidget.config['timeZone'] = profileData?.data?.timezone ?? systemTimezone;

            // eslint-disable-next-line prettier/prettier
            selectedWidget['config']['route'] = {};
            selectedWidget['config']['route']['url'] = `/${currentRealmName}/loadTrend/${wrapperId}/${
                dataGetter['deviceId'] ?? dataGetter['groupId']
            }`;
        }
        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleAggregatedTrends(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        if (selectedWidget?.config) {
            selectedWidget.config['secondary'] = response;
            selectedWidget.config['timeZone'] = profileData?.data?.timezone ?? systemTimezone;
            selectedWidget['config']['route'] = {};
            selectedWidget['config']['route']['url'] = `/${currentRealmName}/aggregatedTrends/${
                dataGetter['deviceId'] ?? dataGetter['groupId']
            }/${widgetId}`;
        }
        detailDispatcher(widgetId, selectedWidget?.config);
    }

    const handleEnergyFlowRoute = (): string => {
        if (selectedNode?.wrapperDeviceType === 'Gateway')
            return `/${currentRealmName}/device/${selectedNode?.id}#devices`;
        return `/${currentRealmName}/group/${selectedNode?.id ?? currentOrg?.id}#devices`;
    };

    function handleEnergyFlow(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        if (selectedWidget?.config) {
            selectedWidget.config['secondary'] = response;
            selectedWidget['config']['route'] = {};

            selectedWidget['config']['route']['url'] = selectedWidget?.config?.multiDevice
                ? `/${currentRealmName}/energyFlowDetails`
                : handleEnergyFlowRoute();
        }
        detailDispatcher(widgetId, selectedWidget?.config);
    }

    function handleWeather(widgetId: any, response: any): void {
        const selectedWidget = JSON.parse(
            JSON.stringify(widgetsClone?.find((item: any) => item?.id === widgetId) ?? {})
        );
        if (selectedWidget?.config) {
            selectedWidget.config['secondary'] = response;
            selectedWidget['config']['route'] = {};
            selectedWidget['config']['route']['url'] = `/${currentRealmName}/weatherDetails`;
        }
        detailDispatcher(widgetId, selectedWidget?.config);
    }

    const response = {
        properties: (apiResponse: any, widgetId: any): any =>
            handleDetailValues(apiResponse[0]?.data?.data, widgetId, 'secondary'),
        settings: (apiResponse: any, widgetId: any): any => {
            const grouped = apiResponse?.[0]?.data?.data ?? {};
            const channels = Object.values(grouped)
                .flat()
                .filter(Boolean)
                .map((item: any) => ({
                    channelId: item?.channelId,
                    // Use mapped name (e.g. "Disabled") if available, else raw value.
                    value: item?.values?.find((v: any) => v?.value === item?.currentValue)?.name ?? item?.currentValue,
                    unit: item?.unit ?? '',
                }));
            handleDetailValues([{ deviceId: dataGetter['deviceId'], channels }], widgetId, 'secondary');
        },
        details: (apiResponse: any, widgetId: any): any =>
            handleDetailValues(apiResponse[0]?.data?.data, widgetId, 'secondary'),
        command_bar: (apiResponse: any, widgetId: any): any => handleCommandBar(widgetId, apiResponse),
        device_list: (apiResponse: any, widgetId: any): any => handleDeviceList(widgetId, apiResponse[0]?.data?.data),
        group_list: (apiResponse: any, widgetId: any): any => handleDeviceList(widgetId, apiResponse[0]?.data?.data),
        gauge: (apiResponse: any, widgetId: any): any => handleGaugeDetails(widgetId, apiResponse[0]?.data?.data ?? []),
        map: (apiResponse: any, widgetId: any): any => handleMap(widgetId, apiResponse[0]?.data?.data ?? []),
        timeline: (apiResponse: any, widgetId: any): any => handleTimelineDetails(widgetId, apiResponse),
        loads: (apiResponse: any, widgetId: any): any => handleDeviceLoads(widgetId, apiResponse[0]?.data?.data ?? []),
        load_groups: (apiResponse: any, widgetId: any): any =>
            handleLoadGroups(widgetId, apiResponse[0]?.data?.data ?? []),
        load_group_trends: (apiResponse: any, widgetId: any): any => handleLoadGroupTrends(widgetId, apiResponse),
        virtual_loads: (apiResponse: any, widgetId: any): any =>
            handleVirtualLoads(widgetId, apiResponse[0]?.data?.data ?? []),
        trends: (apiResponse: any, widgetId: any, extraArgs?: any): any =>
            handleTrends(widgetId, apiResponse[0]?.data?.data ?? [], extraArgs),
        charts: (apiResponse: any, widgetId: any): any =>
            handleChartDetails(widgetId, apiResponse[0]?.data?.data ?? []),
        upcoming: (apiResponse: any, widgetId: any): any => handleUpcoming(widgetId, apiResponse[0]?.data?.data ?? []),
        environmental_stress_monitoring: (
            apiResponse: [{ data?: EnvironmentalMetricsResponse }, { data?: EnvironmentalFilterResponse }],
            widgetId: string
        ): void =>
            handleEnvironmentalStressMonitoring(widgetId, {
                metrics: apiResponse[0]?.data,
                filter: apiResponse[1]?.data,
            }),
        score: (apiResponse: any, widgetId: any): any => handleScore(widgetId, apiResponse[0]?.data ?? {}),
        components: (apiResponse: any, widgetId: any): any =>
            handleComponents(widgetId, apiResponse[0]?.data?.data ?? []),
        consumption_breakdown: (apiResponse: any, widgetId: any): any =>
            handleConsumptionBreakdown(widgetId, apiResponse[0]?.data?.data),
        load_trends: (apiResponse: any, widgetId: any): any => handleLoadTrends(widgetId, apiResponse[0]?.data?.data),
        energy_flow: (apiResponse: any, widgetId: any): any => handleEnergyFlow(widgetId, apiResponse[0]?.data?.data),
        metrics: (apiResponse: any, widgetId: any): any => handleEnergyFlow(widgetId, apiResponse[0]?.data?.data),
        aggregated_trends: (apiResponse: any, widgetId: any): any =>
            handleAggregatedTrends(widgetId, apiResponse[0]?.data?.data),
        weather: (apiResponse: any, widgetId: any): any => handleWeather(widgetId, apiResponse[0]?.data?.data),
        schedule: (apiResponse: any, widgetId: any): any => handleSchedule(widgetId, apiResponse[0]?.data?.data),
    };

    return {
        payload: dataGetter?.['groupId'] || dataGetter?.['deviceId'] ? payload : null,
        response: !isNullOrUndefined(widgetsClone) ? response : '',
    };
};
